import TableCommonHook from '@op/components/TableCommonHook';
import React, { useMemo, useState } from 'react';
import useIntls from '@opm/hooks/useIntls';
import { SortBy } from 'tea-component/lib/table/addons';
import SortBySelect from '@op/components/SelectComponents/SortBySelect';
import './index.scss';
import { StatusTip } from 'tea-component';
import { DBO_V } from '@op/utils/format';
import AdInfoCell from '../AdInfoCell';
import AdsIcon from '@opm/assets/svg/icon-ads.svg';
import AiImg from '@op/assets/svg/icon-ai.svg';
import { AdMetricColumns, ADSortOptions, MARKETING_AI_TAG_COLOR_KEY } from '../../constants';
import BubbleCommon from '@op/components/BubbleCommon';
import { adCreativesDeduplicationTipsMap } from '@opm/components/BubbleCommon/copywritingKey/AdCreatives';
import TagList from '@op/pages/community/video/components/TagList';
import AiContentCell from '../AiContentCell';
import Search from '@op/components/FilterBoxCommon/filters/search';
import { ITaggingInfo } from '@opm/services/competitorsAdCreatives';
import { TOpenWindowsType } from '../type';
import MetricProgress from '@opm/components/MetricProgress';

export interface TableViewProps {
  tableRef: React.RefObject<HTMLDivElement>;
  data: any[];
  total: number;
  maxStats: Record<string, number>;
  pageInfo: { page: number; page_size: number };
  changePageInfo: (pageInfo?: { page: number; page_size: number }) => void;
  loading?: boolean;
  sortBy: SortBy<string>;
  setSortBy: (newSortBy: SortBy<string>) => void;
  type: string;
  open?: (records: any, type?: TOpenWindowsType) => void;
  extInfoMap: Record<string, ITaggingInfo>;
  extInfoLoading?: boolean;
  setSearchVideoTags: (tags: string[]) => void;
}

export default function TableView({
  tableRef,
  data,
  total,
  maxStats,
  pageInfo,
  changePageInfo,
  loading,
  sortBy,
  setSortBy,
  type,
  open,
  extInfoMap,
  extInfoLoading,
  setSearchVideoTags,
}: TableViewProps) {
  const { intl } = useIntls();
  const [tagSearchText, setTagSearchText] = useState('');

  /** 获取某条记录对应的 ext_info */
  function getExtInfo(records: any): ITaggingInfo | undefined {
    const url = records?.video_url;
    return url ? extInfoMap[url] : undefined;
  }

  const columns: any[] = useMemo(() => {
    const list: any[] = [
      // ─── 首列：广告信息（固定左侧） ───
      {
        key: 'app_name',
        header: (
          <div className="dbom-table-header-reviews">
            <BubbleCommon
              tipsMap={adCreativesDeduplicationTipsMap}
              placement="top"
            >
              {intl(type === 'ads' ? 'Ads' : 'Creatives')}
            </BubbleCommon>
            {/* <SortBySelect
              options={ADSortOptions}
              defaultSortBy={sortBy}
              changeSortBy={setSortBy}
            /> */}
          </div>
        ),
        width: 500,
        render: (records: any) => (
          <AdInfoCell
            open={(t: TOpenWindowsType = 'model') => open?.(records, t)}
            records={records}
            type={type}
          />
        ),
        fixed: 'left',
      },

      // ─── 指标列 ───
      ...AdMetricColumns.map((col) => ({
        key: col.key,
        align: 'right',
        header: (
          <div className="dbom-table-header-metrics">
            <img src={AdsIcon} className="dbom-table-header-metrics-icon" />
            {intl(col.text)}
          </div>
        ),
        render: (records: any) => {
          const value = records[col.key];
          const { maxKey } = col;
          const biggest = maxKey ? maxStats[maxKey] || 0 : 0;
          return (
            <div className="community-render-norm">
              <p>{value && value !== -1 ? DBO_V.reviews(value) : '-'}</p>
              {maxKey && (
                <MetricProgress
                  biggest={biggest}
                  count={value === -1 ? 0 : value}
                />
              )}
            </div>
          );
        },
        width: 200,
      })),

      // ─── AI Tags 列（固定） ───
      {
        key: 'ai_tags',
        header: (
          <div className="dbom-table-search-ai-tags">
            <div className="dbo-inlineflex">
              <img src={AiImg} className="dbo-inlineflex-img" />
              {intl('AI Tags')}
            </div>
            <Search
              defaultParams={{
                placeholder: intl('Search AI Tags'),
                onSearch: (value: any) => {
                  const tags = String(value)
                    .split(/;|；/)
                    .map((t: string) => t.trim())
                    .filter(Boolean);
                  setSearchVideoTags(tags);
                  setTagSearchText(value);
                },
                onClear: () => {
                  setSearchVideoTags([]);
                  setTagSearchText('');
                },
                defaultValue: tagSearchText,
              }}
            />
          </div>
        ),
        width: 500,
        render: (records: any) => {
          if (extInfoLoading) {
            return <StatusTip status="loading" loadingText="" />;
          }
          const info = getExtInfo(records);
          if (!info?.video_tags || info.video_tags.length === 0) return '-';
          return (
            <TagList
              list={info.video_tags}
              colorKey={MARKETING_AI_TAG_COLOR_KEY}
            />
          );
        },
      },

      // ─── AI Core Content 列（固定） ───
      {
        key: 'ai_core_content',
        header: (
          <div className="dbo-inlineflex">
            <img src={AiImg} className="dbo-inlineflex-img" />
            {intl('AI Core Content')}
          </div>
        ),
        width: 520,
        render: (records: any) => {
          const info = getExtInfo(records);
          return (
            <AiContentCell
              loading={extInfoLoading}
              summaryList={info?.content_summary}
              labelKey="Core Content"
              noDataText={records?.type?.[0] === 'video' ? 'Unable to generate a summary.' : 'AI summary is not currently supported for this content type.'}
            />
          );
        },
      },

      // ─── AI Hook Content 列（固定） ───
      {
        key: 'ai_hook_content',
        header: (
          <div className="dbo-inlineflex">
            <img src={AiImg} className="dbo-inlineflex-img" />
            {intl('AI Hook Analysis')}
          </div>
        ),
        width: 520,
        render: (records: any) => {
          const info = getExtInfo(records);
          return (
            <AiContentCell
              loading={extInfoLoading}
              summaryList={info?.content_summary}
              labelKey="Hook Content"
              noDataText={records?.type?.[0] === 'video' ? 'Unable to generate a summary.' : 'AI summary is not currently supported for this content type.'}
            />
          );
        },
      },
      {
        key: 'body',
        header: intl('Body'),
        width: 250,
        render: (records: any) => {
          const { body } = records;
          if (!body) return '-';
          return (
            <BubbleCommon showIcon={false} tipsMap={null} placement='top' content={body}>
              <div className="dbom-ai-content-cell" title={body}>
                <div className='dbom-ai-content-cell__text'>{body}</div>
              </div>
            </BubbleCommon>
          );
        },
      },
    ];

    return list;
  }, [intl, sortBy, setSortBy, type, maxStats, extInfoMap, extInfoLoading, tagSearchText]);

  return (
    <div ref={tableRef} className="dbom-competitors-ad-creatives-table-view">
      <TableCommonHook
        className={'dbom-competitors-ad-creatives-table'}
        sortInfo={[sortBy, setSortBy]}
        records={data}
        total={total}
        loading={loading}
        pageInfo={pageInfo}
        setPageInfo={changePageInfo}
        sortColumns={AdMetricColumns.map((item) => item.key)}
        recordKey={(item) => item.creative_id || item.app_name}
        columns={columns}
        tableSticky
        height={500}
        bordered={true}
      />
    </div>
  );
}
